import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from "./components/LanguageSwitcher";
import { Lock, User, ArrowRight, Mail, CheckCircle } from "lucide-react";
import API from "./api";

function Login() {
  const { t } = useTranslation();
  const [loginMode, setLoginMode] = useState("password"); // "password" or "magic"
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const res = await API.post("/login/", { username, password });
      const token = res.data.access;
      const refreshToken = res.data.refresh;
      localStorage.setItem("token", token);
      if (refreshToken) {
        localStorage.setItem("refreshToken", refreshToken);
      }
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(t('login.login_failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleMagicLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    try {
      await API.post("/auth/magic-link/request/", { email });
      setSuccess(t('login.magic_sent_msg'));
    } catch (err) {
      console.error("Magic link error:", err);
      setError(t('login.magic_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      <div className="absolute top-6 right-8 z-40">
        <LanguageSwitcher />
      </div>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex flex-col items-center justify-center mb-4">
          <img src="/logo.png" alt="Robopam Logo" className="w-36 h-36 object-contain drop-shadow-[0_0_25px_rgba(37,99,235,0.4)]" />
        </div>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('login.platform_login')}
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-xl sm:rounded-2xl sm:px-10">

          {/* Tab Navigation */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => { setLoginMode("password"); setError(""); setSuccess(""); }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${loginMode === "password"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <User className="inline-block w-4 h-4 mr-1 -mt-1" />
              {t('login.login_with_password')}
            </button>
            <button
              type="button"
              onClick={() => { setLoginMode("magic"); setError(""); setSuccess(""); }}
              className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-all ${loginMode === "magic"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
                }`}
            >
              <Mail className="inline-block w-4 h-4 mr-1 -mt-1" />
              {t('login.magic_link')}
            </button>
          </div>

          {/* Password Login Form */}
          {loginMode === "password" && (
            <form className="space-y-6" onSubmit={handlePasswordLogin}>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  {t('login.username')}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="username"
                    name="username"
                    type="text"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-lg py-3 border"
                    placeholder={t('login.username_placeholder')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  {t('login.password')}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-lg py-3 border"
                    placeholder={t('login.password_placeholder')}
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${loading ? "opacity-75 cursor-not-allowed" : ""
                  }`}
              >
                {loading ? t('login.logging_in') : t('login.login_btn')}
                {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
              </button>
            </form>
          )}

          {/* Magic Link Form */}
          {loginMode === "magic" && (
            <form className="space-y-6" onSubmit={handleMagicLink}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  {t('login.email')}
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-lg py-3 border"
                    placeholder={t('login.magic_email_placeholder')}
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {t('login.magic_help_text')}
                </p>
              </div>

              {error && (
                <div className="rounded-lg bg-red-50 p-4 border border-red-200">
                  <p className="text-sm font-medium text-red-800">{error}</p>
                </div>
              )}

              {success && (
                <div className="rounded-lg bg-green-50 p-4 border border-green-200 flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-green-800">{success}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={loading || success}
                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all ${loading || success ? "opacity-75 cursor-not-allowed" : ""
                  }`}
              >
                {loading ? t('login.sending') : success ? t('login.sent_success') : t('login.send_magic_btn')}
                {!loading && !success && <Mail className="ml-2 h-4 w-4" />}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              {t('login.no_account')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
